import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { ApiHealthRestService } from './rest/api.health.rest.service';
import {
  BehaviorSubject,
  Subscription,
  timer,
  switchMap,
  from,
  of,
  catchError,
} from 'rxjs';
import { Router } from '@angular/router';
import { IApiHealthStatus } from '../interfaces/IAppHealthStatus.interface';

@Injectable({
  providedIn: 'root',
})
export class ApiHealthService implements OnDestroy {
  private _healthRestService = inject(ApiHealthRestService);
  private _router = inject(Router);

  private _connection = new BehaviorSubject<boolean>(false);
  private healthCheckSubscription: Subscription | null = null;
  private _apiVersion = signal<string | undefined>(undefined);
  public apiVersion = computed(() => this._apiVersion());

  constructor() {
    this.startBackgroundCheck();
  }

  get connection(): BehaviorSubject<boolean> {
    return this._connection;
  }

  /**
   * Manually trigger a health check
   */
  public async getHealth() {
    try {
      const response = await this._healthRestService.getHealth();
      this.processResponse(response);
    } catch {
      this.updateStatus(false);
      this._router.navigateByUrl('/error');
    }
  }

  public updateStatus(status: boolean) {
    this._connection.next(status);
  }

  /**
   * Background check
   */
  private startBackgroundCheck() {
    this.healthCheckSubscription = timer(1000, 10000)
      .pipe(
        switchMap(() =>
          from(this._healthRestService.getHealth()).pipe(
            catchError(() => {
              this.updateStatus(false);
              return of(null);
            }),
          ),
        ),
      )
      .subscribe((response) => {
        if (response) {
          this.processResponse(response);
        }
      });
  }

  /**
   * Shared logic to handle the response
   */
  private processResponse(response: IApiHealthStatus) {
    this.updateStatus(true);
    if (response?.version) {
      this._apiVersion.set(response.version);
    }
  }

  ngOnDestroy(): void {
    if (this.healthCheckSubscription) {
      this.healthCheckSubscription.unsubscribe();
      this.healthCheckSubscription = null;
    }
  }
}
